import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  getBranches,
  getCashRegisters,
  getProducts,
} from "../api";
import CategoryFormModal from "../components/CategoryFormModal";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import { normalizePaginatedResponse } from "../utils/pagination";
import InlineLoading from "../components/InlineLoading";
import useConfirmDialog from '../hooks/useConfirmDialog';
import useProductEvents from '../hooks/useProductEvents';

const CategoriesAdminPage = ({ branchId: branchIdProp, embedded = false }) => {
  const params = useParams();
  const branchId = branchIdProp ?? params.branchId ?? localStorage.getItem("selectedBranchId");
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [cashRegistersByBranch, setCashRegistersByBranch] = useState({});
  const [productCounts, setProductCounts] = useState({ byId: {}, byName: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const { confirm } = useConfirmDialog();
  const { user } = useCurrentUser();
  const productCategoryIndexRef = useRef(new Map());

  const canCreateCategory = hasPermission(user, "category.create") || hasPermission(user, "category.manage");
  const canUpdateCategory = hasPermission(user, "category.update") || hasPermission(user, "category.manage");
  const canDeleteCategory = hasPermission(user, "category.delete") || hasPermission(user, "category.manage");

  const availabilityForCategory = (category) => {
    return (
      category?.CategoryAvailabilities
      || category?.CategoryAvailability
      || []
    );
  };

  const getProductCount = (category) => {
    if (!category) return 0;
    const countById = productCounts.byId[category.id] || 0;
    if (countById > 0) return countById;
    if (!category.name) return countById;
    return productCounts.byName[category.name] || 0;
  };

  const availabilitySummary = (category) => {
    const rows = availabilityForCategory(category);
    if (!rows.length) return "Sin disponibilidad";
    const branchMap = new Map();
    rows.forEach((row) => {
      const branchId = row?.branchId;
      if (!branchId) return;
      const key = String(branchId);
      const entry = branchMap.get(key) || { all: false, ids: new Set() };
      if (!row.cashRegisterId) {
        entry.all = true;
        entry.ids.clear();
      } else if (!entry.all) {
        entry.ids.add(String(row.cashRegisterId));
      }
      branchMap.set(key, entry);
    });

    const getRegistersForBranch = (branchKey) => {
      if (cashRegistersByBranch[branchKey]) return cashRegistersByBranch[branchKey];
      const numericKey = Number(branchKey);
      if (Number.isInteger(numericKey) && cashRegistersByBranch[numericKey]) {
        return cashRegistersByBranch[numericKey];
      }
      return [];
    };

    let registerCount = 0;
    branchMap.forEach((entry, branchKey) => {
      if (entry.all) {
        const registers = getRegistersForBranch(branchKey);
        registerCount += registers.length;
        return;
      }
      registerCount += entry.ids.size;
    });

    return `${branchMap.size} sucursales, ${registerCount} cajas`;
  };

  const fetchData = async () => {
    if (!branchId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
        const [categoryData, branchData, productData] = await Promise.all([
          getCategories({ includeAvailability: true }),
          getBranches({ pageSize: 0 }),
          getProducts(branchId, { pageSize: 0 }),
        ]);
        setCategories(categoryData || []);
        setBranches(normalizePaginatedResponse(branchData).data || []);

      const countsById = {};
      const countsByName = {};
      const nextIndex = new Map();
      const normalizedProducts = normalizePaginatedResponse(productData);
      (normalizedProducts.data || []).forEach((product) => {
        const categoryId = product?.categoryId ?? null;
        const categoryName = typeof product?.category === "string" ? product.category : null;
        if (product?.categoryId) {
          countsById[product.categoryId] = (countsById[product.categoryId] || 0) + 1;
        }
        if (categoryName) {
          countsByName[categoryName] = (countsByName[categoryName] || 0) + 1;
        }
        if (product?.id) {
          nextIndex.set(String(product.id), { categoryId, categoryName });
        }
      });
      setProductCounts({ byId: countsById, byName: countsByName });
      productCategoryIndexRef.current = nextIndex;
    } catch (err) {
      setError(err.message || "Error cargando categorias.");
    } finally {
      setLoading(false);
    }
  };

  const refreshProductCounts = useCallback(async () => {
    if (!branchId) return;
    try {
      const productData = await getProducts(branchId, { pageSize: 0 });
      const normalizedProducts = normalizePaginatedResponse(productData);
      const countsById = {};
      const countsByName = {};
      const nextIndex = new Map();
      (normalizedProducts.data || []).forEach((product) => {
        const categoryId = product?.categoryId ?? null;
        const categoryName = typeof product?.category === "string" ? product.category : null;
        if (product?.categoryId) {
          countsById[product.categoryId] = (countsById[product.categoryId] || 0) + 1;
        }
        if (categoryName) {
          countsByName[categoryName] = (countsByName[categoryName] || 0) + 1;
        }
        if (product?.id) {
          nextIndex.set(String(product.id), { categoryId, categoryName });
        }
      });
      setProductCounts({ byId: countsById, byName: countsByName });
      productCategoryIndexRef.current = nextIndex;
    } catch (err) {
      console.error("Error refreshing product counts:", err);
    }
  }, [branchId]);

  useEffect(() => {
    fetchData();
  }, [branchId]);

  const handleProductEvent = useCallback((event) => {
    const action = event?.action;
    if (!action) return;
    if (!["create-product", "update-product", "delete-product"].includes(action)) return;
    const productId = event?.productId ?? event?.product?.id ?? event?.productPatch?.id;
    if (!productId) return;
    const key = String(productId);
    const previous = productCategoryIndexRef.current.get(key) || { categoryId: null, categoryName: null };
    if (action === "delete-product") {
      if (!previous.categoryId && !previous.categoryName) return;
      setProductCounts((prev) => {
        const nextById = { ...prev.byId };
        const nextByName = { ...prev.byName };
        if (previous.categoryId != null) {
          const count = Math.max((nextById[previous.categoryId] || 0) - 1, 0);
          nextById[previous.categoryId] = count;
        }
        if (previous.categoryName) {
          const count = Math.max((nextByName[previous.categoryName] || 0) - 1, 0);
          nextByName[previous.categoryName] = count;
        }
        return { byId: nextById, byName: nextByName };
      });
      productCategoryIndexRef.current.delete(key);
      return;
    }

    const product = event?.product || event?.productPatch;
    if (!product) {
      refreshProductCounts();
      return;
    }
    const nextCategoryId = product?.categoryId ?? null;
    const nextCategoryName = typeof product?.category === "string" ? product.category : null;

    if (action === "create-product") {
      setProductCounts((prev) => {
        const nextById = { ...prev.byId };
        const nextByName = { ...prev.byName };
        if (nextCategoryId != null) {
          nextById[nextCategoryId] = (nextById[nextCategoryId] || 0) + 1;
        }
        if (nextCategoryName) {
          nextByName[nextCategoryName] = (nextByName[nextCategoryName] || 0) + 1;
        }
        return { byId: nextById, byName: nextByName };
      });
      productCategoryIndexRef.current.set(key, {
        categoryId: nextCategoryId,
        categoryName: nextCategoryName,
      });
      return;
    }

    const changedId = previous.categoryId !== nextCategoryId;
    const changedName = previous.categoryName !== nextCategoryName;
    if (!changedId && !changedName) {
      productCategoryIndexRef.current.set(key, {
        categoryId: nextCategoryId,
        categoryName: nextCategoryName,
      });
      return;
    }

    setProductCounts((prev) => {
      const nextById = { ...prev.byId };
      const nextByName = { ...prev.byName };
      if (previous.categoryId != null) {
        const count = Math.max((nextById[previous.categoryId] || 0) - 1, 0);
        nextById[previous.categoryId] = count;
      }
      if (previous.categoryName) {
        const count = Math.max((nextByName[previous.categoryName] || 0) - 1, 0);
        nextByName[previous.categoryName] = count;
      }
      if (nextCategoryId != null) {
        nextById[nextCategoryId] = (nextById[nextCategoryId] || 0) + 1;
      }
      if (nextCategoryName) {
        nextByName[nextCategoryName] = (nextByName[nextCategoryName] || 0) + 1;
      }
      return { byId: nextById, byName: nextByName };
    });
    productCategoryIndexRef.current.set(key, {
      categoryId: nextCategoryId,
      categoryName: nextCategoryName,
    });
  }, [refreshProductCounts]);

  useProductEvents(branchId, handleProductEvent);

  useEffect(() => {
    const fetchRegisters = async () => {
      if (!branches.length) return;
      const map = {};
      await Promise.all(
        branches.map(async (branch) => {
          try {
            const data = await getCashRegisters(branch.id, { pageSize: 0 });
            map[branch.id] = normalizePaginatedResponse(data).data || [];
          } catch (err) {
            map[branch.id] = [];
          }
        })
      );
      setCashRegistersByBranch(map);
    };
    fetchRegisters();
  }, [branches]);

  const closeForm = () => {
    setFormOpen(false);
    setEditingCategory(null);
  };

  const handleSave = async (payload) => {
    setSaving(true);
    setError("");
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, payload);
      } else {
        await createCategory(payload);
      }
      await fetchData();
      closeForm();
    } catch (err) {
      setError(err.message || "Error guardando categoria.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category) => {
    if (!category) return;
    const approved = await confirm({
      title: 'Eliminar categoría',
      message: `Eliminar categoria "${category.name}"?`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    setSaving(true);
    setError("");
    try {
      await deleteCategory(category.id);
      await fetchData();
    } catch (err) {
      setError(err.message || "Error eliminando categoria.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={embedded ? "" : "p-6"}>
        <InlineLoading label="Cargando categorias" />
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "p-6 max-w-5xl mx-auto"}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Categorias</h1>
          <p className="text-sm text-gray-600">
            Crea y asigna categorias a sucursales y cajas.
          </p>
        </div>
        {canCreateCategory && (
          <button
            onClick={() => {
              setEditingCategory(null);
              setFormOpen(true);
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Nueva categoria
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="grid gap-4">
        {categories.map((category) => {
          const productCount = getProductCount(category);
          const canDelete = productCount === 0;
          return (
            <div key={category.id} className="bg-white rounded shadow p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div>
                  <h2 className="text-lg font-semibold">{category.name}</h2>
                  <p className="text-sm text-gray-600">
                    Estado: {category.isActive ? "Activa" : "Inactiva"} · Productos: {productCount}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {availabilitySummary(category)}
                  </p>
                </div>
                <div className="flex gap-2">
                  {canUpdateCategory && (
                    <button
                      className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
                      onClick={() => {
                        setEditingCategory(category);
                        setFormOpen(true);
                      }}
                    >
                      Editar
                    </button>
                  )}
                  {canDeleteCategory && (
                    <button
                      className="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 text-sm disabled:opacity-50"
                      onClick={() => handleDelete(category)}
                      disabled={!canDelete || saving}
                      title={canDelete ? "" : "La categoria tiene productos asociados"}
                    >
                      Eliminar
                    </button>
                  )}
                </div>
              </div>
              {category.description && (
                <p className="text-sm text-gray-700 mt-2">
                  {category.description}
                </p>
              )}
            </div>
          );
        })}
        {!categories.length && (
          <div className="bg-white rounded shadow p-6 text-sm text-gray-600">
            No hay categorias creadas.
          </div>
        )}
      </div>

      <CategoryFormModal
        isOpen={formOpen}
        onClose={closeForm}
        onSave={handleSave}
        branches={branches}
        cashRegistersByBranch={cashRegistersByBranch}
        initialData={editingCategory}
        isSaving={saving}
      />
    </div>
  );
};

export default CategoriesAdminPage;
